'use client'

import { useEffect, useMemo, useRef } from 'react'

import { MediaImage as Image } from '@/components/media-image'

/**
 * Image bbox: lon 144.8-148.0, lat -37.2 to -38.5
 * x% = (lon - 144.8) / 3.2 * 100
 * y% = (|lat| - 37.2) / 1.3 * 100
 */
type MapLocation = { name: string; x: number; y: number; isHome?: boolean }

type Dir = 'right' | 'left' | 'bottom' | 'top'

const DIR_CLASS: Record<Dir, string> = {
  right: 'top-1/2 left-full ml-2 -translate-y-1/2',
  left: 'top-1/2 right-full mr-2 -translate-y-1/2',
  bottom: 'top-full left-1/2 mt-1.5 -translate-x-1/2',
  top: 'bottom-full left-1/2 mb-1.5 -translate-x-1/2',
}

/** Approximate label bounding box in percentage units for each direction */
function labelBox(x: number, y: number, dir: Dir): [number, number, number, number] {
  // [minX, minY, maxX, maxY] - label is roughly 12% wide, 3% tall
  const w = 12
  const h = 3
  switch (dir) {
    case 'right':
      return [x + 1, y - h / 2, x + 1 + w, y + h / 2]
    case 'left':
      return [x - 1 - w, y - h / 2, x - 1, y + h / 2]
    case 'bottom':
      return [x - w / 2, y + 1, x + w / 2, y + 1 + h]
    case 'top':
      return [x - w / 2, y - 1 - h, x + w / 2, y - 1]
  }
}

function boxHitsDot(box: [number, number, number, number], dx: number, dy: number) {
  return dx >= box[0] - 1 && dx <= box[2] + 1 && dy >= box[1] - 1 && dy <= box[3] + 1
}

function boxInBounds(box: [number, number, number, number]) {
  return box[0] >= -2 && box[1] >= -2 && box[2] <= 102 && box[3] <= 102
}

/** Best label direction for a location, avoiding overlap with other dots */
function pickLabelDirection(loc: MapLocation, others: readonly MapLocation[]): Dir {
  const preferred: Dir[] =
    loc.x < 50 ? ['right', 'bottom', 'top', 'left'] : ['left', 'bottom', 'top', 'right']

  for (const dir of preferred) {
    const box = labelBox(loc.x, loc.y, dir)
    if (!boxInBounds(box)) continue
    const hits = others.some((o) => boxHitsDot(box, o.x, o.y))
    if (!hits) return dir
  }

  for (const dir of preferred) {
    if (boxInBounds(labelBox(loc.x, loc.y, dir))) return dir
  }

  return loc.x < 50 ? 'right' : 'left'
}

interface ServiceAreaMapProps {
  locations: readonly MapLocation[]
  activeLocation?: string | null
  onSelectLocation?: (name: string) => void
}

export function ServiceAreaMap({
  locations,
  activeLocation,
  onSelectLocation,
}: ServiceAreaMapProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const labelDirections = useMemo<Dir[]>(
    () =>
      locations.map((loc, i) =>
        pickLabelDirection(
          loc,
          locations.filter((_, j) => j !== i)
        )
      ),
    [locations]
  )

  useEffect(() => {
    const el = mapRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('map-revealed')
          observer.unobserve(el)
        }
      },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={mapRef}
      className="group relative aspect-[3/2] w-full overflow-hidden rounded-sm lg:absolute lg:inset-0 lg:aspect-auto"
    >
      {/* Satellite base layer */}
      <Image
        src="/images/satellite-eastern-vic.webp"
        alt="Satellite view of eastern Victoria showing the Dandenong Ranges, Cardinia Shire, Yarra Ranges, and Gippsland"
        fill
        sizes="(max-width: 1024px) 100vw, 50vw"
        className="object-cover"
      />

      {/* Dark brand overlay to integrate with site theme */}
      <div className="absolute inset-0 bg-[#1a0e20]/60 mix-blend-multiply" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a0e20] via-transparent to-[#1a0e20]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a0e20]/30 via-transparent to-[#1a0e20]/50" />

      {/* Purple tint over the ranges */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 40% 30% at 22% 55%, rgba(133, 46, 133, 0.25) 0%, transparent 100%)',
        }}
      />

      {/* Location pins */}
      {locations.map((loc, i) => {
        const isActive = activeLocation === loc.name

        return (
          <button
            key={loc.name}
            type="button"
            onClick={() => onSelectLocation?.(loc.name)}
            className="map-pin absolute z-10 cursor-pointer border-none bg-transparent p-2"
            style={{
              left: `${loc.x}%`,
              top: `${loc.y}%`,
              animationDelay: `${i * 120 + 400}ms`,
              transform: 'translate(-50%, -50%)',
            }}
            aria-label={`${loc.name}${loc.isHome ? ' (home base)' : ''}`}
          >
            {/* Pulse rings (active only) */}
            {isActive && (
              <>
                <span className="absolute -inset-1 animate-[map-pulse_1.5s_ease-in-out_infinite] rounded-full border-2 border-[#8bfa6e]/60" />
                <span className="absolute -inset-4 animate-[map-pulse_1.5s_ease-in-out_0.3s_infinite] rounded-full border border-[#8bfa6e]/30" />
              </>
            )}

            {/* Pin dot */}
            <span
              className={`relative block rounded-full transition-all duration-300 ${
                isActive
                  ? 'size-3 bg-[#8bfa6e] shadow-[0_0_16px_rgba(149,248,98,0.9)]'
                  : 'size-2 bg-white/60 shadow-[0_0_4px_rgba(255,255,255,0.3)] hover:bg-white/90'
              }`}
            />

            {/* Label (active only) */}
            <span
              className={`font-heading pointer-events-none absolute text-xs font-bold tracking-wider whitespace-nowrap uppercase transition-all duration-300 ${
                isActive ? 'text-[#8bfa6e] opacity-100' : 'opacity-0'
              } ${DIR_CLASS[labelDirections[i]]}`}
            >
              {loc.name}
            </span>
          </button>
        )
      })}

      {/* Coverage area glow */}
      <div
        className="absolute inset-0 opacity-0 transition-opacity duration-1000 [.map-revealed_&]:opacity-100"
        style={{
          background:
            'radial-gradient(ellipse 55% 50% at 30% 58%, rgba(149, 248, 98, 0.08) 0%, transparent 70%)',
        }}
      />
    </div>
  )
}
